import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  AlertTriangle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Filter,
  Eye,
  Brain,
  Shield,
  FileCheck,
  Scale,
  ArrowLeft
} from 'lucide-react';

const AutonomyExplainability = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [packets, setPackets] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  
  const [filters, setFilters] = useState({
    subject: searchParams.get('subject') || '',
    autonomy_level: searchParams.get('autonomy_level') || '',
    skip: parseInt(searchParams.get('skip')) || 0,
    limit: 20
  });

  const fetchPackets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('skip', filters.skip);
      params.set('limit', filters.limit);
      if (filters.subject) params.set('subject', filters.subject);
      if (filters.autonomy_level) params.set('autonomy_level', filters.autonomy_level);
      
      const res = await api.get(`/admin/autonomy/explainability?${params.toString()}`);
      setPackets(res.data.packets || []);
    } catch (error) {
      console.error('Failed to fetch explainability packets:', error);
      toast.error('Failed to load explainability audit');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPackets();
  }, [fetchPackets]);

  const handlePageChange = (direction) => {
    const newSkip = direction === 'next' 
      ? filters.skip + filters.limit 
      : Math.max(0, filters.skip - filters.limit);
    setFilters({ ...filters, skip: newSkip });
  };

  const autonomyLevelConfig = {
    'full_auto': { color: 'bg-green-100 text-green-800', label: 'Full Auto' },
    'supervised': { color: 'bg-blue-100 text-blue-800', label: 'Supervised' },
    'approval_required': { color: 'bg-yellow-100 text-yellow-800', label: 'Approval Required' },
    'manual_only': { color: 'bg-gray-100 text-gray-800', label: 'Manual Only' }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="autonomy-explainability-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/autonomy" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Explainability Audit</h1>
                <p className="text-sm text-gray-500">AI decision audit trail and explanations</p>
              </div>
            </div>
            <Button onClick={fetchPackets} variant="outline" size="sm" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium">Filters:</span>
              
              <Input
                placeholder="Subject..."
                value={filters.subject}
                onChange={(e) => {
                  const newFilters = { ...filters, subject: e.target.value, skip: 0 };
                  setFilters(newFilters);
                }}
                className="w-40"
                data-testid="filter-subject"
              />

              <select
                value={filters.autonomy_level}
                onChange={(e) => {
                  const newFilters = { ...filters, autonomy_level: e.target.value, skip: 0 };
                  setFilters(newFilters);
                }}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="filter-autonomy-level"
              >
                <option value="">All Levels</option>
                <option value="full_auto">Full Auto</option>
                <option value="supervised">Supervised</option>
                <option value="approval_required">Approval Required</option>
                <option value="manual_only">Manual Only</option>
              </select>

              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setFilters({ subject: '', autonomy_level: '', skip: 0, limit: 20 });
                  setSearchParams({});
                }}
              >
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Packets List */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Decision Audit Trail
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : packets.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Scale className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No explainability packets found</p>
              </div>
            ) : (
              <div className="divide-y">
                {packets.map((packet) => (
                  <div 
                    key={packet.id} 
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setExpandedId(expandedId === packet.id ? null : packet.id)}
                    data-testid={`packet-${packet.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${packet.requires_human_review ? 'bg-amber-100' : 'bg-green-100'}`}>
                          {packet.requires_human_review ? (
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                          ) : (
                            <Brain className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{packet.subject || 'Unknown Subject'}</span>
                            <Badge className={autonomyLevelConfig[packet.autonomy_level]?.color || 'bg-gray-100'}>
                              {autonomyLevelConfig[packet.autonomy_level]?.label || packet.autonomy_level}
                            </Badge>
                            {packet.requires_human_review && (
                              <Badge className="bg-amber-100 text-amber-800">Needs Review</Badge>
                            )}
                            {packet.human_override && (
                              <Badge className="bg-purple-100 text-purple-800">Overridden</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            Action: <span className="font-medium">{packet.chosen_action || 'N/A'}</span>
                            {packet.action_confidence && (
                              <span className="ml-2 text-gray-400">
                                ({(packet.action_confidence * 100).toFixed(1)}% confidence)
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-400">
                        <p>{packet.created_at ? new Date(packet.created_at).toLocaleString() : '-'}</p>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedId === packet.id && (
                      <div className="mt-4 pt-4 border-t space-y-4">
                        {/* Features Used */}
                        {packet.features && Object.keys(packet.features).length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              Features Considered
                            </p>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <pre className="text-xs overflow-auto max-h-32">
                                {JSON.stringify(packet.features, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}

                        {/* Constraints Applied */}
                        {packet.constraints_applied && packet.constraints_applied.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              Constraints Applied
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {packet.constraints_applied.map((constraint, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {constraint}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Policies Checked */}
                        {packet.policies_checked && packet.policies_checked.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <FileCheck className="w-3 h-3" />
                              Policies Checked
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {packet.policies_checked.map((policy, idx) => (
                                <Badge key={idx} className="bg-blue-50 text-blue-700 text-xs">
                                  {policy}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Alternatives Considered */}
                        {packet.alternatives && packet.alternatives.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2">Alternative Actions Considered</p>
                            <div className="space-y-2">
                              {packet.alternatives.map((alt, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                                  <span>{typeof alt === 'string' ? alt : alt.action || JSON.stringify(alt)}</span>
                                  {alt.confidence && (
                                    <span className="text-gray-400 text-xs">
                                      {(alt.confidence * 100).toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* IDs */}
                        <div className="grid grid-cols-2 gap-4 text-xs text-gray-400">
                          {packet.decision_id && (
                            <p>Decision ID: {packet.decision_id}</p>
                          )}
                          {packet.action_id && (
                            <p>Action ID: {packet.action_id}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            Showing {packets.length} results
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.skip === 0}
              onClick={() => handlePageChange('prev')}
              data-testid="prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={packets.length < filters.limit}
              onClick={() => handlePageChange('next')}
              data-testid="next-page"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AutonomyExplainability;
